// src/App.js - FINAL COMPLETE VERSION with Impressum & Datenschutz
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { setPageMeta } from './utils/seo';
import ErrorBoundary from './components/ErrorBoundary';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './Dashboard';
import PublicDownload from './PublicDownload';
import Upload from './Upload';
import BlockWarning from './BlockWarning';
import Settings from './Settings';
import AdminPanel from './AdminPanel';
import MaintenanceScreen from './components/MaintenanceScreen';
import LockdownScreen from './components/LockdownScreen';
import MaintenanceBanner from './components/MaintenanceBanner';
import Impressum   from './Impressum';
import Datenschutz from './Datenschutz';
import AGB         from './AGB';
import Billing     from './Billing';
import Widerruf       from './Widerruf';
import TwoFAChallenge from './TwoFAChallenge';
import AuthAction     from './AuthAction';
import EmailAuthForm  from './components/EmailAuthForm';

// Forced-Logout-Overlay mit 5s-Countdown
function ForcedLogoutOverlay({ message, onDone }) {
  const [count, setCount] = React.useState(5);
  React.useEffect(() => {
    const iv = setInterval(() => {
      setCount(c => {
        if (c <= 1) { clearInterval(iv); onDone(); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [onDone]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 999999, background: 'rgba(10,10,15,0.97)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: '1.5rem', pointerEvents: 'all',
      fontFamily: 'var(--font-head, system-ui, sans-serif)',
    }}>
      <div style={{ fontSize: '3rem' }}>🔒</div>
      <h2 style={{ color: '#fff', fontSize: '1.4rem', fontWeight: 800, margin: 0, textAlign: 'center', padding: '0 1.5rem' }}>
        Du wurdest ausgeloggt
      </h2>
      <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem', margin: 0, textAlign: 'center', maxWidth: '400px', padding: '0 1.5rem', lineHeight: 1.6 }}>
        {message}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <div style={{
          width: '38px', height: '38px', borderRadius: '50%',
          background: 'rgba(0,212,255,0.15)', border: '2px solid rgba(0,212,255,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--cyan, #00d4ff)', fontWeight: 800, fontSize: '1rem',
        }}>{count}</div>
        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>
          Weiterleitung zur Anmeldung…
        </span>
      </div>
      <button
        onClick={onDone}
        style={{
          padding: '0.6rem 1.5rem', background: 'var(--cyan, #00d4ff)', border: 'none',
          borderRadius: '7px', color: '#0a0a0a', fontWeight: 700, fontSize: '0.85rem',
          cursor: 'pointer',
        }}
      >
        Jetzt anmelden
      </button>
    </div>
  );
}

function App() {
  // Auth State
  const [token, setToken] = useState(null);
  const [forcedLogout, setForcedLogout] = useState(null); // null | string (Grund)
  // Prevent horizontal scroll on mobile
  useEffect(() => {
    document.documentElement.style.overflowX = 'hidden';
    document.body.style.overflowX = 'hidden';
    document.body.style.width = '100%';
    document.body.style.maxWidth = '100vw';
  }, []);

  const [user, setUser] = useState(null);

  // Active team storage context — persists across view switches + page refreshes
  const [activeTeamContext, setActiveTeamContext] = useState(() => {
    try {
      const saved = sessionStorage.getItem('avs_team_context');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const setActiveTeamContextPersisted = (ctx) => {
    setActiveTeamContext(ctx);
    try {
      if (ctx) sessionStorage.setItem('avs_team_context', JSON.stringify(ctx));
      else sessionStorage.removeItem('avs_team_context');
    } catch {}
  };

  // View State - Dashboard oder Upload
  // If URL is /team/<uuid>, open Dashboard directly to avoid flash of Upload view
  const [currentView, setCurrentView] = useState(() =>
    /^\/team\/[0-9a-f-]{36}$/.test(window.location.pathname) ? 'dashboard' : 'upload'
  );

  // Folder Context — lifted so Upload knows which folder is active
  const [currentFolderContext, setCurrentFolderContext] = useState({ id: null, name: null });
  
  // Stats State
  const [stats, setStats] = useState({
    totalFiles: '10M+',
    totalStorage: '500TB',
    totalUsers: '150+'
  });
  
  // Particles
  const [particles, setParticles] = useState([]);

  // Activity Tracking State
  const lastActivityRef = useRef(Date.now());
  const sessionWarningRef = useRef(false);
  const [, forceWarning] = useState(0);
  const [showBlockWarning, setShowBlockWarning] = useState(false);
  const [systemStatus, setSystemStatus] = useState(null);
  const [bannerStatus, setBannerStatus] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [statusChecked, setStatusChecked] = useState(false);
  const [tokenInitialized, setTokenInitialized] = useState(false);
  const [requires2Fa, setRequires2Fa] = useState(false);
  
  // Particles initialization
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const newParticles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 5,
      duration: 3 + Math.random() * 4
    }));
    setParticles(newParticles);
  }, []);

  // ============================================
  // THEME LOADER - Load user theme preference
  // ============================================
  useEffect(() => {
    if (token) {
      fetch('/api/users/profile', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.user.theme) {
          applyTheme(data.user.theme);
        }
      })
      .catch(err => console.error('Theme load error:', err));
    }
  }, [token]);

  const applyTheme = (theme) => {
    if (theme === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.classList.toggle('dark', prefersDark);
    } else {
      document.documentElement.classList.toggle('dark', theme === 'dark');
    }
  };

  // ============================================
  // TOKEN AUTO-REFRESH
  // ============================================
  useEffect(() => {
    if (!token) return;

    const refreshToken = async () => {
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) return;
        
        const tokenData = JSON.parse(atob(tokenParts[1]));
        const expiresAt = tokenData.exp * 1000;
        const now = Date.now();
        const timeUntilExpiry = expiresAt - now;

        if (timeUntilExpiry < 10 * 60 * 1000 && timeUntilExpiry > 0) {
          console.log('[TOKEN] Auto-refreshing token... (expires in ' + Math.round(timeUntilExpiry / 60000) + ' minutes)');
          
          const response = await fetch('/api/auth/refresh', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (response.ok) {
            const data = await response.json();
            setToken(data.token);
            lastActivityRef.current = Date.now();
            console.log('[TOKEN] Token refreshed successfully!');
          } else {
            console.warn('[TOKEN] Refresh failed, user needs to re-login');
            handleLogout();
          }
        }
      } catch (error) {
        console.error('[TOKEN] Refresh error:', error);
      }
    };

    const interval = setInterval(refreshToken, 5 * 60 * 1000);
    refreshToken();
  return () => clearInterval(interval);
  }, [token]);

  // ============================================
  // TOKEN INITIALIZATION
  // ============================================
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token'); // legacy URL fallback

    if (urlParams.get('auth') === '2fa_required') {
      // 2FA Challenge — pre-auth cookie ist gesetzt, zeige Challenge-Seite
      window.history.replaceState({}, document.title, '/');
      setRequires2Fa(true);
      setTokenInitialized(true);
    } else if (tokenFromUrl && window.location.pathname !== '/auth-action') {
      // Legacy: Token direkt in URL (Fallback) — nicht in localStorage speichern
      // (auf /auth-action ist "token" der E-Mail-Verify/Reset-Token, kein Session-JWT)
      setToken(tokenFromUrl);
      window.history.replaceState({}, document.title, '/');
      setTokenInitialized(true);
    } else {
      // Token aus httpOnly Cookie holen — gilt für OAuth-Redirect UND Page-Refresh
      if (urlParams.get('auth') === 'success') {
        window.history.replaceState({}, document.title, '/');
      }
      fetch('/api/auth/session', { credentials: 'include' })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data && data.token) setToken(data.token);
        })
        .catch(() => {})
        .finally(() => setTokenInitialized(true));
    }
  }, []);

  // ============================================
  // TEAM URL CONTEXT LOADER — /team/:spaceId
  // ============================================
  useEffect(() => {
    if (!tokenInitialized || !token) return;
    const match = window.location.pathname.match(/^\/team\/([0-9a-f-]{36})$/);
    if (!match) return;
    const spaceId = match[1];
    // Already loaded for this exact space?
    if (activeTeamContext && activeTeamContext.teamSpaceId === spaceId) {
      setCurrentView('dashboard');
      return;
    }
    fetch('/api/team/spaces', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const owned    = data.owned    || [];
        const memberOf = data.memberOf || [];
        const isOwner  = owned.some(s => s.id === spaceId);
        const space    = [...owned, ...memberOf].find(s => s.id === spaceId);
        if (!space) {
          // No access — go back to private storage
          try { sessionStorage.removeItem('avs_team_context'); } catch {}
          window.location.href = '/';
          return;
        }
        setActiveTeamContextPersisted({
          teamSpaceId:   space.id,
          spaceName:     space.name,
          ownerUsername: space.owner_username,
          ownerId:       space.owner_id,
          isSeparate:    !!space.is_separate,
          permissions:   isOwner ? 'read_write' : (space.my_permissions || 'read'),
          isOwner,
          quotaBytes:    space.quota_bytes  || 0,
          usedBytes:     space.used_bytes   || 0,
        });
        setCurrentView('dashboard');
      })
      .catch(() => { window.location.href = '/'; });
  }, [tokenInitialized, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // LOAD USER & CHECK ACCOUNT STATUS
  // ============================================
  useEffect(() => {
    if (!tokenInitialized) return;
    if (token) {
      fetch('/api/auth/me', {
        headers: { 'Authorization': 'Bearer ' + token }
      })
        .then(res => {
          if (!res.ok) {
            if (res.status === 401) {
              handleLogout();
              return null;
            }
            throw new Error('Failed to fetch user');
          }
          return res.json();
        })
        .then(data => {
          if (!data) return;
          
          // pending_deletion + cancel-deletion ENTFERNT — Löschung ist jetzt sofort (DSGVO Art.17)
          
          if (data.status === 'user_closed') {
            // Account closed — logout silently
            handleLogout();
            return;
          }
          
          if (data.blocked || data.status === 'pending' || data.status === 'permanently_blocked') {
            setShowBlockWarning(true);
            setUser(data);
          } else if (data.username) {
            setUser(data);
          } else {
            handleLogout();
          }
        })
        .catch(err => {
          console.error('Auth error:', err);
          handleLogout();
        })
        .finally(() => {
          setAuthChecked(true);
        });
    } else {
      setAuthChecked(true);
    }
  }, [token, tokenInitialized]);

  // ============================================
  // PER-ROUTE META TAGS
  // ============================================
  useEffect(() => {
    if (!token) {
      setPageMeta({
        title: 'Günstiger Cloud Speicher mit Verschlüsselung | AVS Cloud',
        description: 'AVS Cloud: DSGVO-konformer Cloud-Speicher aus Deutschland ab 0 €. AES-256-GCM-Verschlüsselung, Virenscan, günstiger als Google Drive. Jetzt kostenlos starten.',
        path: '/',
      });
    }
  }, [token]);

  // ============================================
  // LOAD GLOBAL STATS
  // ============================================
  useEffect(() => {
    fetch('/api/files/stats/global')
      .then(res => res.json())
      .then(data => {
        if (!data.totalFiles && !data.totalUsers) return;
        setStats({
          totalFiles: formatNumber(data.totalFiles || 0),
          totalStorage: formatBytes(data.totalStorage || 0),
          totalUsers: (data.totalUsers || 0).toString() + '+'
        });
      })
      .catch(err => console.error('Stats error:', err));
  }, []);

  // ============================================
  // ACTIVITY TRACKER & AUTO-LOGOUT
  // ============================================
  useEffect(() => {
    if (!token) return;

    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      if (sessionWarningRef.current) { sessionWarningRef.current = false; }
    };

    const events = ['keydown', 'mousedown', 'touchstart'];
    events.forEach(event => document.addEventListener(event, updateActivity));

    const checkInactivity = setInterval(() => {
      const inactiveTime = Date.now() - lastActivityRef.current;
      const tenMinutes  = 10 * 60 * 1000;
      const nineMinutes =  9 * 60 * 1000;

      if (inactiveTime >= tenMinutes) {
        handleLogout();
        // Inactivity logout — silent
      } else if (inactiveTime >= nineMinutes && !sessionWarningRef.current) {
        sessionWarningRef.current = true;
        if (window.confirm('Deine Session läuft in 1 Minute ab. Möchtest du eingeloggt bleiben?')) {
          updateActivity();
        }
      }
    }, 30000);

    return () => {
      events.forEach(event => document.removeEventListener(event, updateActivity));
      clearInterval(checkInactivity);
    };
  }, [token]);

  // System-Status polling (alle 10 Sekunden)
  useEffect(() => {
    let firstCheck = true;
    const checkStatus = async () => {
      try {
        const r = await fetch('/api/health/status', { credentials: 'include' });
        if (r.status === 503) {
          const d = await r.json();
          setSystemStatus(d);
          setBannerStatus(null);
        } else {
          const d = await r.json();
          setSystemStatus(null);
          if (d.maintenance_banner_active && d.maintenance_banner) {
            setBannerStatus({ banner: d.maintenance_banner });
          } else {
            setBannerStatus(null);
          }
        }
      } catch { setSystemStatus(null); }
      finally {
        if (firstCheck) { firstCheck = false; setStatusChecked(true); }
      }
    };
    checkStatus();
    const interval = setInterval(checkStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M+';
    if (num >= 1000)    return (num / 1000).toFixed(1) + 'K+';
    return num.toString();
  };

  const handleLogin  = () => { window.location.href = '/api/auth/discord'; };

  const handleLogout = () => {
    // Clear state immediately — don't block on backend response
    setToken(null);
    setUser(null);
    setCurrentView('upload');
    setActiveTeamContextPersisted(null);
    // Fire-and-forget: clears auth_token cookie on server (no authMiddleware required)
    fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    }).catch(() => {});
  };

  // Globaler Fetch-Interceptor — erkennt 401 "revoked" und zeigt Forced-Logout-Screen
  const tokenRef2 = React.useRef(token);
  React.useEffect(() => { tokenRef2.current = token; }, [token]);
  React.useEffect(() => {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if (response.status === 401 && tokenRef2.current) {
        try {
          const clone = response.clone();
          const data  = await clone.json().catch(() => ({}));
          // Nur bei expliziter Revokation (nicht bei normalem 401 z.B. ohne Token)
          if (data.error && (data.error.includes('revoked') || data.error.includes('Session expired'))) {
            setForcedLogout('Deine Sitzung wurde von einem anderen Gerät oder dem Administrator beendet.');
          }
        } catch {}
      }
      return response;
    };
    return () => { window.fetch = originalFetch; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  const ProtectedRoute = ({ children }) => {
    if (!authChecked) return <div style={{ background: '#0a0a0f', minHeight: '100vh' }} />;
    if (!token) return <Navigate to="/" replace />;
    return children;
  };

  if (!authChecked || !statusChecked) {
    return <div style={{ background: '#0a0a0f', minHeight: '100vh' }} />;
  }

    if (requires2Fa) {
      return (
        <TwoFAChallenge onSuccess={(t) => { setToken(t); setRequires2Fa(false); }} />
      );
    }

    return (
    <ErrorBoundary>
      {bannerStatus && <MaintenanceBanner message={bannerStatus.banner} />}
    <Router>
      <Routes>
        {/* ============================================ */}
        {/* PUBLIC ROUTES                               */}
        {/* ============================================ */}
        <Route path="/share/:shareId" element={<PublicDownload />} />
        <Route path="/impressum"      element={<Impressum />} />
        <Route path="/datenschutz"    element={<Datenschutz />} />
        <Route path="/agb"            element={<AGB />} />
        <Route path="/widerruf"       element={<Widerruf />} />
        <Route path="/auth-action"    element={<AuthAction />} />

        {/* ============================================ */}
        {/* PROTECTED ROUTES                            */}
        {/* ============================================ */}
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings token={token} onLogout={handleLogout} />
          </ProtectedRoute>
        } />

        <Route path="/billing" element={
          <ProtectedRoute>
            <Billing token={token} />
          </ProtectedRoute>
        } />
        
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminPanel token={token} />
          </ProtectedRoute>
        } />

        {/* ============================================ */}
        {/* MAIN APP ROUTE                              */}
        {/* ============================================ */}
        <Route path="/*" element={
          <>
            {showBlockWarning ? (
              // BLOCK WARNING
              <BlockWarning 
                user={user} 
                token={token} 
                onLogout={handleLogout} 
                onContinue={() => setShowBlockWarning(false)} 
              />
            ) : !token ? (
              // ============================================
              // LOGIN VIEW
              // ============================================
              <div style={{minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',position:'relative',zIndex:1,padding:'1rem 1rem 3rem'}}>
                <div style={{width:'100%',maxWidth:'420px',animation:'fadeUp 0.6s ease both'}}>

                  {/* Logo + Wordmark */}
                  <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'12px',marginBottom:'2.5rem'}}>
                    <img src="/avs-logo.png" alt="AquaV" className="avs-logo-img" />
                    <div className="avs-wordmark">
                      Aqua<span className="av">V</span>
                      <span className="sub">Cloud</span>
                    </div>
                  </div>

                  {/* Headline */}
                  <div style={{textAlign:'center',marginBottom:'2rem'}}>
                    <p className="avs-label" style={{justifyContent:'center',display:'flex',alignItems:'center',gap:'8px',marginBottom:'0.75rem'}}>
                      <span style={{width:'5px',height:'5px',borderRadius:'50%',background:'var(--cyan)',boxShadow:'0 0 8px var(--cyan)',display:'inline-block',animation:'pulse 2.2s ease-in-out infinite'}}></span>
                      Persönlicher Cloud-Speicher
                    </p>
                    <h1 style={{fontFamily:'var(--font-head)',fontWeight:900,fontSize:'clamp(2rem, 6vw, 2.8rem)',letterSpacing:'-0.04em',lineHeight:1,color:'var(--white)',marginBottom:'0.5rem'}}>
                      Sicher. Schnell.<br/><span style={{color:'var(--cyan)'}}>Deine Daten.</span>
                    </h1>
                    <p style={{color:'var(--muted)',fontSize:'0.87rem',lineHeight:1.7}}>
                      Melde dich an und verwalte deine Dateien mit AVS Cloud.
                    </p>
                  </div>

                  {/* Card */}
                  <div className="avs-card" style={{padding:'1.75rem',boxShadow:'var(--shadow-card)'}}>

                    {/* Stats */}
                    <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'0.5rem',paddingBottom:'1.4rem',borderBottom:'1px solid var(--border)',marginBottom:'1.4rem'}}>
                      {[
                        {val:stats.totalFiles,   label:'Dateien',  color:'var(--cyan)'},
                        {val:stats.totalStorage, label:'Speicher', color:'var(--cyan-light)'},
                        {val:stats.totalUsers,   label:'Nutzer',   color:'var(--blue-mid)'}
                      ].map(({val,label,color}) => (
                        <div key={label} style={{textAlign:'center'}}>
                          <div style={{fontFamily:'var(--font-head)',fontWeight:800,fontSize:'1.25rem',color}}>{val}</div>
                          <div style={{fontFamily:'var(--font-mono)',fontSize:'0.6rem',color:'var(--muted)',letterSpacing:'0.1em',textTransform:'uppercase',marginTop:'2px'}}>{label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Login / Registrierung */}
                    <EmailAuthForm
                      onDiscordLogin={handleLogin}
                      onLoginSuccess={(t) => setToken(t)}
                      onRequires2Fa={() => setRequires2Fa(true)}
                    />

                    {/* Footer links */}
                    <p style={{textAlign:'center',marginTop:'1rem',fontSize:'0.72rem',color:'var(--muted)'}}>
                      Ein Service von{' '}
                      <a href="https://aquav-services.de" target="_blank" rel="noopener noreferrer"
                         style={{color:'var(--cyan)',fontWeight:600}}>
                        AquaV Services
                      </a>
                      {' · '}
                      <a href="/impressum"
                         style={{color:'var(--muted)',textDecoration:'underline',textUnderlineOffset:'3px'}}>
                        Impressum
                      </a>
                      {' · '}
                      <a href="/datenschutz"
                         style={{color:'var(--muted)',textDecoration:'underline',textUnderlineOffset:'3px'}}>
                        Datenschutz
                      </a>
                      {' · '}
                      <a href="/agb"
                         style={{color:'var(--muted)',textDecoration:'underline',textUnderlineOffset:'3px'}}>
                        AGB
                      </a>
                      {' · '}
                      <a href="/widerruf"
                         style={{color:'var(--muted)',textDecoration:'underline',textUnderlineOffset:'3px'}}>
                        Widerruf
                      </a>
                    </p>
                  </div>
                </div>

                {/* ============================================ */}
                {/* SEO/GEO CONTENT — visible, indexable        */}
                {/* ============================================ */}
                <div style={{width:'100%',maxWidth:'680px',marginTop:'3.5rem',display:'flex',flexDirection:'column',gap:'2.5rem',animation:'fadeUp 0.8s 0.2s ease both',opacity:0,animationFillMode:'forwards'}}>

                  {/* About */}
                  <section>
                    <h2 style={{fontFamily:'var(--font-head)',fontWeight:700,fontSize:'1rem',color:'var(--cyan)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:'0.6rem'}}>Über AVS Cloud</h2>
                    <p style={{color:'var(--muted)',fontSize:'0.85rem',lineHeight:1.75}}>
                      AVS Cloud ist ein <strong style={{color:'var(--white)'}}>günstiger Cloud-Speicher aus Deutschland</strong>, betrieben von AquaV Services (Mert Munz, Hamburg).
                      Jede Datei wird mit <strong style={{color:'var(--white)'}}>AES-256-GCM verschlüsselt</strong> gespeichert und bei jedem Upload automatisch auf Viren geprüft.
                      Der Betrieb erfolgt vollständig <strong style={{color:'var(--white)'}}>DSGVO-konform</strong> auf EU-Servern in den Niederlanden.
                      Zuletzt aktualisiert: <time dateTime="2026-06-08">08.06.2026</time>.
                    </p>
                  </section>

                  <div style={{height:'1px',background:'linear-gradient(to right,transparent,rgba(18,112,196,0.15),transparent)'}}/>

                  {/* Features */}
                  <section>
                    <h2 style={{fontFamily:'var(--font-head)',fontWeight:700,fontSize:'1rem',color:'var(--cyan)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:'0.75rem'}}>Features</h2>
                    <ul style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:'0.45rem',listStyle:'none',padding:0}}>
                      {[
                        'AES-256-GCM Verschlüsselung',
                        'Automatischer Virenscan (ClamAV + VirusTotal)',
                        'DSGVO-konformer Betrieb in der EU',
                        'Server in den Niederlanden',
                        'Discord OAuth2 Login',
                        'Passwortgeschützte Share-Links',
                        'Ablaufdatum & Download-Limit für Links',
                        'Günstiger als Google Drive und Dropbox',
                        'Stripe-Zahlungen (Kreditkarte, etc.)',
                        'Admin-Panel & Nutzerverwaltung',
                      ].map(f => (
                        <li key={f} style={{display:'flex',alignItems:'center',gap:'8px',color:'var(--muted)',fontSize:'0.82rem'}}>
                          <span style={{width:'5px',height:'5px',borderRadius:'50%',background:'var(--cyan)',flexShrink:0}}/>
                          {f}
                        </li>
                      ))}
                    </ul>
                  </section>

                  <div style={{height:'1px',background:'linear-gradient(to right,transparent,rgba(18,112,196,0.15),transparent)'}}/>

                  {/* Comparison */}
                  <section>
                    <h2 style={{fontFamily:'var(--font-head)',fontWeight:700,fontSize:'1rem',color:'var(--cyan)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:'0.75rem'}}>Vergleich: AVS Cloud vs. Alternativen</h2>
                    <div style={{overflowX:'auto'}}>
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8rem',color:'var(--muted)'}}>
                        <thead>
                          <tr style={{borderBottom:'1px solid rgba(18,112,196,0.15)'}}>
                            {['Merkmal','AVS Cloud','Google Drive','Dropbox','OneDrive'].map(h => (
                              <th key={h} style={{padding:'0.5rem 0.75rem',textAlign:'left',fontWeight:600,color:h==='AVS Cloud'?'var(--cyan)':'var(--muted)',whiteSpace:'nowrap'}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            ['Ende-zu-Ende Verschlüsselung','AES-256-GCM','Nein','Nein','Nein'],
                            ['Virenscan bei Upload','ClamAV + VirusTotal','Nein','Nein','Nein'],
                            ['DSGVO (EU-Server)','Ja (NL)','Eingeschränkt','Eingeschränkt','Nein (US)'],
                            ['Kostenloser Speicher','5 GB','15 GB','2 GB','5 GB'],
                            ['Zusatzspeicher','ab 0,40 €/10 GB','ab 1,99 €/100 GB','ab 9,99 €/2 TB','ab 2,00 €/100 GB'],
                          ].map(([feat,...vals]) => (
                            <tr key={feat} style={{borderBottom:'1px solid rgba(18,112,196,0.07)'}}>
                              <td style={{padding:'0.45rem 0.75rem',fontWeight:500,color:'var(--white)',whiteSpace:'nowrap'}}>{feat}</td>
                              {vals.map((v,i) => (
                                <td key={i} style={{padding:'0.45rem 0.75rem',color:i===0?'var(--cyan-light)':'var(--muted)'}}>{v}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  <div style={{height:'1px',background:'linear-gradient(to right,transparent,rgba(18,112,196,0.15),transparent)'}}/>

                  {/* FAQ */}
                  <section>
                    <h2 style={{fontFamily:'var(--font-head)',fontWeight:700,fontSize:'1rem',color:'var(--cyan)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:'0.75rem'}}>Häufige Fragen</h2>
                    <div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
                      {[
                        {q:'Was ist AVS Cloud?',a:'AVS Cloud ist ein günstiger, DSGVO-konformer Cloud-Speicher aus Deutschland mit AES-256-GCM-Verschlüsselung und automatischem Virenscan.'},
                        {q:'Ist AVS Cloud günstiger als Google Drive?',a:'Ja. Erweiterter Speicher kostet ab 0,40 € pro 10 GB/Monat – deutlich günstiger als Google Drive, Dropbox oder OneDrive.'},
                        {q:'Ist AVS Cloud DSGVO-konform?',a:'Ja. Der Betrieb erfolgt durch ein deutsches Unternehmen (AquaV Services, Hamburg) auf EU-Servern in den Niederlanden.'},
                        {q:'Wie sicher ist AVS Cloud?',a:'Jede Datei wird mit AES-256-GCM verschlüsselt. Jeder Upload wird automatisch mit ClamAV und VirusTotal auf Schadsoftware geprüft.'},
                        {q:'Kann ich AVS Cloud kostenlos nutzen?',a:'Ja. Das kostenlose Paket bietet 5 GB Speicher ohne Kreditkartenpflicht nach Freischaltung durch den Betreiber.'},
                        {q:'Kann ich Dateien sicher teilen?',a:'Ja. Share-Links können mit Passwortschutz, Ablaufdatum und Download-Limit versehen werden.'},
                      ].map(({q,a}) => (
                        <details key={q} style={{borderBottom:'1px solid rgba(18,112,196,0.1)',paddingBottom:'0.75rem'}}>
                          <summary style={{color:'var(--white)',fontSize:'0.85rem',fontWeight:600,cursor:'pointer',listStyle:'none',display:'flex',justifyContent:'space-between',alignItems:'center',gap:'1rem'}}>
                            {q}<span style={{color:'var(--cyan)',flexShrink:0,fontSize:'1rem'}}>+</span>
                          </summary>
                          <p style={{color:'var(--muted)',fontSize:'0.82rem',lineHeight:1.7,marginTop:'0.5rem'}}>{a}</p>
                        </details>
                      ))}
                    </div>
                  </section>

                </div>
              </div>

            ) : currentView === 'dashboard' ? (
              // ============================================
              // DASHBOARD VIEW
              // ============================================
              <div style={{minHeight:'100vh',position:'relative',zIndex:1}}>
                <Dashboard
                  token={token}
                  user={user}
                  onBackToUpload={() => setCurrentView('upload')}
                  onLogout={handleLogout}
                  activeTeamContext={activeTeamContext}
                  onTeamContextChange={setActiveTeamContextPersisted}
                  bannerStatus={bannerStatus}
                  onFolderContextChange={(id, name) => setCurrentFolderContext({ id, name })}
                />
              </div>

            ) : (
              // ============================================
              // UPLOAD VIEW
              // ============================================
              <div style={{minHeight:'100vh',position:'relative',zIndex:1}}>
                <Upload
                  token={token}
                  user={user}
                  onBackToDashboard={() => setCurrentView('dashboard')}
                  teamContext={activeTeamContext}
                  currentFolderContext={currentFolderContext}
                />
              </div>
            )}
          </>
        } />
      </Routes>
    </Router>
      {systemStatus?.code === 'SYSTEM_LOCKDOWN' && user !== null && !user.is_owner && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, pointerEvents: 'all' }}>
          <LockdownScreen message={systemStatus.message} since={systemStatus.since} onLogout={handleLogout} />
        </div>
      )}
      {systemStatus?.code === 'MAINTENANCE' && user !== null && !user.is_owner && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, pointerEvents: 'all' }}>
          <MaintenanceScreen message={systemStatus.message} since={systemStatus.since} until={systemStatus.until} onLogout={handleLogout} />
        </div>
      )}
      {forcedLogout && <ForcedLogoutOverlay message={forcedLogout} onDone={handleLogout} />}
    </ErrorBoundary>
  );
}

export default App;
